(() => {
  'use strict';
  if (window.__ibShellCoordinatorV57) return;
  window.__ibShellCoordinatorV57 = true;

  const html = document.documentElement;
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let rafId = 0;
  let scrollTimer = 0;
  let scanTrustedQueued = false;

  const trustedSelector = [
    '[id*="trustbadge" i]', '[class*="trustbadge" i]', '[id*="trustedshops" i]',
    '[class*="trustedshops" i]', '[id*="etrusted" i]', '[class*="etrusted" i]',
    '[data-testid*="trustbadge" i]', 'iframe[src*="trustedshops" i]',
    'iframe[src*="etrusted" i]', 'iframe[title*="trusted shops" i]'
  ].join(',');
  const overlaySelector = [
    '#Details-menu-drawer-container[open]', 'dialog[open]', '[role="dialog"][aria-modal="true"]',
    'cart-drawer[open]', 'cart-drawer[aria-hidden="false"]', '.cart-drawer.is-open',
    'search-modal[open]', '.search-modal[open]', 'theme-drawer[open]',
    '.menu-drawer[open]', '.menu-drawer.is-open'
  ].join(',');
  const obstructionSelector = [
    '.sticky-add-to-cart', '[data-sticky-add-to-cart]', '[data-sticky-atc]',
    '[class*="cookie" i]', '[id*="cookie" i]', '[class*="consent" i]', '[id*="consent" i]'
  ].join(',');
  const relevantSelector = `${trustedSelector},${overlaySelector},${obstructionSelector},footer`;

  const isVisible = (element) => {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 2 || rect.height <= 2) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number.parseFloat(style.opacity || '1') !== 0;
  };

  const protectedHostSelector = [
    'html', 'body', 'main', 'header', 'footer', '#MainContent', '#header-group',
    '.page-wrapper', '.shopify-section', '.product-information',
    '.product-information__grid', '.product-details',
    '[data-testid="product-information-details"]', '.group-block',
    '.group-block-content', '.cart-page', 'theme-drawer', 'dialog'
  ].join(',');

  const fixedHost = (element) => {
    let node = element;
    while (node instanceof HTMLElement && node !== document.body) {
      if (node.matches(protectedHostSelector)) return null;
      const position = getComputedStyle(node).position;
      if (position === 'sticky') return null;
      if (position === 'fixed' && isVisible(node)) return node;
      node = node.parentElement;
    }
    return null;
  };

  const clearInvalidTrustedFloaters = () => {
    document.querySelectorAll('.ib-trustedshops-floating').forEach((host) => {
      if (host.matches(protectedHostSelector) || getComputedStyle(host).position !== 'fixed') {
        host.classList.remove('ib-trustedshops-floating');
      }
    });
  };

  const markTrustedFloaters = (root = document) => {
    clearInvalidTrustedFloaters();
    if (mobileQuery.matches) return false;
    const candidates = [];
    if (root instanceof Element && root.matches(trustedSelector)) candidates.push(root);
    root.querySelectorAll?.(trustedSelector).forEach((candidate) => candidates.push(candidate));
    let found = false;
    candidates.forEach((candidate) => {
      const host = fixedHost(candidate);
      if (!host) return;
      host.classList.add('ib-trustedshops-floating');
      found = true;
    });
    return found;
  };

  const keyboardOpen = () => {
    if (!mobileQuery.matches) return false;
    const active = document.activeElement;
    const editable = Boolean(active && (active.matches('input,textarea,select,[contenteditable="true"]') || active.closest?.('[contenteditable="true"]')));
    return editable || Boolean(window.visualViewport && window.visualViewport.height < window.innerHeight * 0.72);
  };

  const overlayOpen = () => Array.from(document.querySelectorAll(overlaySelector)).some(isVisible);

  const bottomObstruction = () => {
    if (!mobileQuery.matches) return 0;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || html.clientHeight;
    const viewportWidth = window.innerWidth || html.clientWidth;
    let height = 0;
    document.querySelectorAll(obstructionSelector).forEach((element) => {
      if (!isVisible(element) || element.closest('#whatsapp-desktop')) return;
      const style = getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = element.getBoundingClientRect();
      if (
        rect.bottom >= viewportHeight - 4 &&
        rect.width >= Math.min(280, viewportWidth * 0.55) &&
        rect.height >= 30 && rect.height <= viewportHeight * 0.48
      ) height = Math.max(height, Math.ceil(rect.height));
    });
    return height;
  };

  const refresh = ({ scanTrusted = false } = {}) => {
    if (scanTrusted) markTrustedFloaters();
    const obscured = overlayOpen() || keyboardOpen();
    const reservedBottom = bottomObstruction();
    html.classList.toggle('ib-overlay-active', obscured);
    html.classList.toggle('ib-floating-ui-obscured', obscured);
    html.style.setProperty('--ib-floating-reserved-bottom', `${reservedBottom}px`);
    const whatsapp = document.getElementById('whatsapp-desktop');
    const label = whatsapp?.getAttribute('aria-label');
    if (whatsapp && label && !whatsapp.title) whatsapp.title = label;
  };

  const schedule = (scanTrusted = false) => {
    scanTrustedQueued = scanTrustedQueued || scanTrusted;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      const shouldScanTrusted = scanTrustedQueued;
      scanTrustedQueued = false;
      refresh({ scanTrusted: shouldScanTrusted });
    });
  };

  const scheduleScroll = () => {
    if (scrollTimer) return;
    scrollTimer = window.setTimeout(() => {
      scrollTimer = 0;
      schedule(false);
    }, 240);
  };

  const observeRelevantInsertions = () => {
    if (!document.body || !('MutationObserver' in window)) return;
    new MutationObserver((mutations) => {
      let relevant = false;
      let trusted = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (!mobileQuery.matches && (node.matches(trustedSelector) || node.querySelector(trustedSelector))) trusted = true;
          if (node.matches(relevantSelector) || node.querySelector(relevantSelector)) relevant = true;
        }
      }
      if (relevant) schedule(trusted);
    }).observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    clearInvalidTrustedFloaters();
    refresh();
    document.addEventListener('click', () => schedule(false), { passive: true });
    document.addEventListener('toggle', () => schedule(false), true);
    document.addEventListener('focusin', () => schedule(false));
    document.addEventListener('focusout', () => schedule(false));
    document.addEventListener('shopify:section:load', () => schedule(true));
    ['theme-drawer:open', 'theme-drawer:close', 'cart-section:restored'].forEach((eventName) =>
      document.addEventListener(eventName, () => schedule(false))
    );
    window.addEventListener('pageshow', () => schedule(true));
    window.addEventListener('resize', () => schedule(false), { passive: true });
    window.addEventListener('orientationchange', () => schedule(false), { passive: true });
    window.visualViewport?.addEventListener('resize', () => schedule(false), { passive: true });
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', () => schedule(true));
    else mobileQuery.addListener?.(() => schedule(true));

    if (document.querySelector('main[data-template^="product"]')) window.addEventListener('scroll', scheduleScroll, { passive: true });
    const footer = document.querySelector('footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        html.classList.toggle('ib-footer-in-view', entries.some((entry) => entry.isIntersecting));
      }, { rootMargin: '0px 0px 140px 0px', threshold: 0.01 }).observe(footer);
    }
    observeRelevantInsertions();

    const scan = () => schedule(true);
    if ('requestIdleCallback' in window) requestIdleCallback(scan, { timeout: 2500 });
    else window.setTimeout(scan, 1200);
  };

  const startAfterLoad = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1200 });
    else window.setTimeout(start, 250);
  };

  if (document.readyState === 'complete') startAfterLoad();
  else window.addEventListener('load', startAfterLoad, { once: true });
})();
